import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ThumbsUp, ThumbsDown, TrendingDown, Loader2 } from 'lucide-react';
import { getGuideVoteSummary } from '@/lib/api';

// Builds a map: `${category.id}-${idx}` -> "Category › Section title"
function buildTitleMap(t) {
  const map = {};
  const cats = t('guide.categories', { returnObjects: true });
  if (Array.isArray(cats)) {
    cats.forEach((cat) => {
      (cat.sections || []).forEach((s, idx) => {
        map[`${cat.id}-${idx}`] = `${cat.title} › ${s.title}`;
      });
    });
  }
  return map;
}

export const GuideVotesWidget = ({ t }) => {
  const [loading, setLoading] = useState(true);
  const [worst, setWorst] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const titleMap = buildTitleMap(t);
    getGuideVoteSummary()
      .then(({ data }) => {
        if (cancelled) return;
        const sections = (data.sections || [])
          .map((s) => ({
            ...s,
            title: titleMap[s.section_id] || t('admin.guideVotes.unknownSection'),
            score: (s.up || 0) - (s.down || 0),
            total: (s.up || 0) + (s.down || 0),
          }))
          .filter((s) => s.total > 0)
          // worst first: lowest net score, then most down-votes
          .sort((a, b) => a.score - b.score || b.down - a.down)
          .slice(0, 5);
        setWorst(sections);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <Card data-testid="guide-votes-widget">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-rose-500" />
          {t('admin.guideVotes.title')}
        </CardTitle>
        <CardDescription>{t('admin.guideVotes.desc')}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground" data-testid="guide-votes-loading">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {t('admin.guideVotes.loading')}
          </div>
        ) : worst.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="guide-votes-empty">
            {t('admin.guideVotes.empty')}
          </p>
        ) : (
          <ol className="space-y-2" data-testid="guide-votes-list">
            {worst.map((s, i) => (
              <li
                key={s.section_id}
                className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3"
                data-testid={`guide-vote-row-${s.section_id}`}
              >
                <span className="text-sm font-bold text-muted-foreground w-5 shrink-0">{i + 1}.</span>
                <span className="flex-1 text-sm font-medium truncate" title={s.title}>
                  {s.title}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                  <ThumbsUp className="h-3.5 w-3.5" /> {s.up}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 dark:text-rose-400">
                  <ThumbsDown className="h-3.5 w-3.5" /> {s.down}
                </span>
                <span
                  className={`text-xs font-bold w-12 text-right ${
                    s.score < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500'
                  }`}
                  title={t('admin.guideVotes.score')}
                >
                  {s.score > 0 ? `+${s.score}` : s.score}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
